import React, { useState, useMemo, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { Search, MapPin, X } from 'lucide-react';
import { getCitiesGroupedByState } from '../data/indianCities';

/**
 * Typing-search "Where" combobox.
 *
 * - Inline input lets the user type to filter cities (and states).
 * - The results popup renders into a React Portal so it can never be clipped
 *   by any ancestor that uses `overflow-hidden` (the hero section, the search bar,
 *   rounded-full clipping, etc.).
 * - State labels show in light grey above each group; cities listed alphabetically.
 * - Returns { city } via onSelect.
 */
export const AreaSearchInput = ({ value, onSelect, onClear, placeholder = 'Type city or area…' }) => {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0, width: 320 });
  const triggerRef = useRef(null);
  const popupRef = useRef(null);
  const inputRef = useRef(null);

  const display = value || '';

  // --- close on outside click (covers both trigger + portal popup)
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      const t = e.target;
      if (triggerRef.current && triggerRef.current.contains(t)) return;
      if (popupRef.current && popupRef.current.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  // --- compute popup position relative to viewport, anchored to the trigger
  const updatePosition = () => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const desiredWidth = Math.max(r.width, 360);
    const margin = 12;
    let left = r.left;
    // keep within viewport
    if (left + desiredWidth + margin > window.innerWidth) {
      left = Math.max(margin, window.innerWidth - desiredWidth - margin);
    }
    setPos({ top: r.bottom + 8, left, width: desiredWidth });
  };

  useLayoutEffect(() => {
    if (!open) return undefined;
    updatePosition();
    const onScroll = () => updatePosition();
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [open]);

  const grouped = useMemo(() => getCitiesGroupedByState(), []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return grouped;
    return grouped
      .map(({ state, cities }) => ({
        state,
        cities: cities.filter(
          (c) => c.toLowerCase().includes(q) || state.toLowerCase().includes(q)
        ),
      }))
      .filter(({ cities }) => cities.length > 0);
  }, [grouped, query]);

  const totalShown = filtered.reduce((s, g) => s + g.cities.length, 0);

  const handleSelect = (city) => {
    onSelect && onSelect({ city, area: '' });
    setQuery('');
    setOpen(false);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    setQuery('');
    onClear && onClear();
    inputRef.current?.focus();
  };

  // --- portal popup
  const popup = open && typeof document !== 'undefined' ? createPortal(
    <div
      ref={popupRef}
      style={{ position: 'fixed', top: pos.top, left: pos.left, width: pos.width, zIndex: 60 }}
      className="bg-white rounded-2xl shadow-2xl border border-gray-200 max-h-[50vh] overflow-y-auto"
      data-testid="area-search-dropdown"
    >
      {totalShown === 0 ? (
        <div className="p-8 text-center text-sm text-gray-500" data-testid="area-search-empty">
          <Search className="h-5 w-5 mx-auto text-gray-300 mb-2" />
          No locations match "{query}"
        </div>
      ) : (
        filtered.map(({ state, cities }) => (
          <div key={state}>
            <div className="sticky top-0 bg-white px-4 py-2 text-[11px] font-bold uppercase tracking-[0.18em] text-gray-400 border-b border-gray-100 z-10">
              {state}
            </div>
            {cities.map((c) => (
              <button
                key={c}
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => handleSelect(c)}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-[#FFE5EE] transition-colors"
                data-testid={`city-option-${c.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <MapPin className="h-4 w-4 text-[#FF0059] flex-shrink-0" />
                <span className="flex-1 font-semibold text-sm text-black truncate">{c}</span>
                <span className="text-xs text-gray-400">{state}</span>
              </button>
            ))}
          </div>
        ))
      )}
    </div>,
    document.body
  ) : null;

  return (
    <div ref={triggerRef} className="relative" data-testid="area-search-combobox">
      <div className="flex items-center gap-2">
        <input
          ref={inputRef}
          type="text"
          value={open ? query : display || query}
          onChange={(e) => { setQuery(e.target.value); if (!open) setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder={placeholder}
          className="w-full border-0 p-0 h-auto bg-transparent text-base font-bold text-black focus:outline-none focus:ring-0 placeholder:text-gray-400 placeholder:font-medium"
          data-testid="area-search-input"
          autoComplete="off"
        />
        {(value || query) && (
          <button
            type="button"
            onClick={handleClear}
            className="p-0.5 rounded-full hover:bg-gray-100 transition-colors flex-shrink-0"
            aria-label="Clear"
            data-testid="area-search-clear"
          >
            <X className="h-3.5 w-3.5 text-gray-400" />
          </button>
        )}
      </div>
      {popup}
    </div>
  );
};
